// src/components/DashboardCards/InventoryCard.js
// Dashboard entry point to the inventory surfaces (Inventory Health #65,
// Inventory Performance #42 "dashboard summary module"). Reads the two cached
// overview endpoints and links each figure to the tab that explains it.
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../../context/AuthContext';
import { useAmazonStatus } from '../../hooks/useAmazonStatus';
import * as ihmApi from '../../pages/SellerIntelligence/InventoryHealth/services/inventoryHealthApi';
import * as ipmApi from '../../pages/SellerIntelligence/InventoryPerformance/services/inventoryPerformanceApi';
import styles from './InventoryCard.module.css';

const IHM = '/dashboard/seller-intelligence/inventory-health';
const IPM = '/dashboard/seller-intelligence/inventory-performance';

const INR = (paise) => {
  if (paise == null) return '—';
  const rupees = paise / 100;
  if (rupees >= 10_000_000) return `₹${(rupees / 10_000_000).toFixed(2)} Cr`;
  if (rupees >= 100_000) return `₹${(rupees / 100_000).toFixed(1)} L`;
  return `₹${Math.round(rupees).toLocaleString('en-IN')}`;
};
const rupees = (value) => (value == null ? null : Math.round(value * 100));

const InventoryCard = () => {
  const authCtx = useContext(AuthContext);
  const navigate = useNavigate();
  const { connected, loading: checking } = useAmazonStatus(authCtx.token);
  const [health, setHealth] = useState(null);
  const [perf, setPerf] = useState(null);

  useEffect(() => {
    if (!connected || !authCtx.token) return;
    let cancelled = false;
    ihmApi.getOverview(authCtx.token)
      .then((body) => { if (!cancelled) setHealth(body.data); })
      .catch(() => { if (!cancelled) setHealth(false); });
    ipmApi.getOverview(authCtx.token)
      .then((body) => { if (!cancelled) setPerf(body.data); })
      .catch(() => { if (!cancelled) setPerf(false); });
    return () => { cancelled = true; };
  }, [connected, authCtx.token]);

  if (checking || !connected) return null;

  const t = health?.state_totals || {};
  const atRisk = (t.critical || 0) + (t.warning || 0) + (t.out_of_stock || 0);
  const metrics = [
    { label: 'Inventory value', value: health ? INR(health.capital_position?.total_value_minor) : null,
      hint: 'at cost', to: `${IHM}?tab=value` },
    { label: 'At risk of stock-out', value: health ? atRisk : null,
      hint: `${t.out_of_stock || 0} already out`, to: `${IHM}?tab=stock-out-risk`, alert: atRisk > 0 },
    { label: 'Excess SKUs', value: perf ? perf.totals?.excess_skus : null,
      hint: 'over cover', to: `${IPM}?tab=excess` },
    { label: 'Stranded SKUs', value: perf ? perf.totals?.stranded_skus : null,
      hint: perf ? `${perf.totals?.stranded_units || 0} units can't sell` : '', to: `${IPM}?tab=stranded`,
      alert: (perf?.totals?.stranded_skus || 0) > 0 },
    { label: 'Storage next month', value: perf ? INR(rupees(perf.storage_bill?.next_month_total)) : null,
      hint: 'estimated bill', to: `${IPM}?tab=storage` },
  ];

  return (
    <section className={styles.card}>
      <div className={styles.header}>
        <h3 className={styles.title}>Inventory</h3>
        <div className={styles.links}>
          <button className={styles.link} onClick={() => navigate(IHM)}>Inventory Health →</button>
          <button className={styles.link} onClick={() => navigate(IPM)}>Performance →</button>
          <button className={styles.link} onClick={() => navigate('/dashboard/seller-intelligence/restock')}>Restock →</button>
        </div>
      </div>
      <div className={styles.grid}>
        {metrics.map((m) => (
          <button key={m.label} className={`${styles.metric} ${m.alert ? styles.metricAlert : ''}`}
                  onClick={() => navigate(m.to)}>
            <span className={styles.metricLabel}>{m.label}</span>
            <span className={styles.metricValue}>
              {m.value === null ? <span className={styles.loading}>…</span> : (m.value ?? '—')}
            </span>
            {m.hint && <span className={styles.metricHint}>{m.hint}</span>}
          </button>
        ))}
      </div>
      {(health === false || perf === false) && (
        <div className={styles.note}>Some inventory figures couldn't load — open the pages for details.</div>
      )}
    </section>
  );
};

export default InventoryCard;
